import { Card, Spinner } from '@wundo/ui';
import { Clock, Search, ShieldCheck, TrendingUp } from 'lucide-react';
import type { TypicalQuestionsApiResponse } from '@/types/dashboard.ts';
import { formatTimeAgo } from './questionCategories.ts';

export interface TypicalQuestionsCardProps {
  insights: TypicalQuestionsApiResponse | undefined;
  loading: boolean;
}

/**
 * What people keep asking, across every domain — one LLM-written paragraph and
 * the themes under it.
 *
 * It loads on its own, after the counts: the summary takes a model call, and
 * the rest of the page should not wait for it.
 */
export function TypicalQuestionsCard({ insights, loading }: TypicalQuestionsCardProps) {
  return (
    <Card tone='quiet' className='flex flex-col gap-4'>
      <h2 className='flex items-center gap-2 text-md font-semibold text-ink'>
        <Search className='size-4' aria-hidden='true' />
        Typical questions
      </h2>

      {loading && (
        <p className='flex items-center gap-2 text-sm text-ink-muted'>
          <Spinner />
          Loading insights…
        </p>
      )}

      {!loading &&
        insights !== undefined &&
        (insights.summary === null ? (
          <p className='text-sm text-ink-muted italic'>
            Not enough data yet to generate cross-category insights. Keep coaching to
            unlock patterns.
          </p>
        ) : (
          <>
            <p className='text-sm text-ink-body'>{insights.summary}</p>
            {insights.typical_questions !== null &&
              insights.typical_questions.length > 0 && (
                <div className='flex flex-col gap-2'>
                  <p className='flex items-center gap-1.5 text-xs font-bold tracking-widest text-ink-muted uppercase'>
                    <TrendingUp className='size-3.5' aria-hidden='true' />
                    Recurring themes
                  </p>
                  <ul className='flex list-disc flex-col gap-1.5 pl-4 text-sm text-ink-body'>
                    {insights.typical_questions.map((question) => (
                      <li key={question}>{question}</li>
                    ))}
                  </ul>
                </div>
              )}
          </>
        ))}

      <div className='flex items-center justify-between gap-3 border-t border-line pt-3 text-xs text-ink-muted'>
        <span className='flex items-center gap-1.5'>
          {insights?.generated_at !== undefined && (
            <>
              <Clock className='size-3.5' aria-hidden='true' />
              {formatTimeAgo(insights.generated_at)}
            </>
          )}
        </span>
        <span className='flex items-center gap-1.5'>
          <ShieldCheck className='size-3.5' aria-hidden='true' />
          Privacy-protected
        </span>
      </div>
    </Card>
  );
}
